import { Injectable } from '@angular/core';
import { ITask, ITaskParameter } from '@fi-shared/fi-task-shared/fi-task-shared';
import { SideListOptionType } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { isNullOrUndefined } from 'util';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { Subject } from 'rxjs';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiTasksViewMode } from '@fi-shared/fi-task-shared/fi-task-enums';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { FiSnackbarComponent } from '@fi-shared/fi-snackbar/fi-snackbar.component';
import { FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';

@Injectable()
export class FiTaskHelperService {
    taskCompletedChanged$ = new Subject<{ taskId: number; isCompleted: boolean }>();

    constructor(
        private userDataService: FiUserDataService,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
        private fiSnackBarService: FiSnackBarService,
    ) {}

    isPrivate(task: ITask): boolean {
        return task.IsPrivate && task.CreatedByUserId == this.userDataService.getUserId();
    }

    isTaskToBeDone(task: ITask) {
        return !task.IsCompleted && !task.IsArchived && (task.IsPrivate || task.HasViewAccess); //
        // Assigned to me or Private tasks which not completed and not archived
    }

    // obsolete for web, will remove while removing/changing cordova mobile app code
    isCreatedByMeForOthers(task: ITask): boolean {
        return false;
        //return task.Attributes.indexOf(FiTaskAttributeType.CreatedByMeForOthers) !== -1;
    }

    getTaskClass(task: ITask) {
        return {
            'task-completed': task.IsCompleted,
            'task-has-deadline': !!task.Deadline,
            'task-deadline-passed': task.IsDeadlinePassed,
            'task-has-comments': !!task.CommentsCount,
        };
    }
    getDeadlineText(task: ITask) {
        if (!task.Deadline) return this.appTextService.get('task.no_deadline');

        return (
            this.appTextService.get('task.deadline') +
            ' ' +
            this.fiFormatDateService.formatDate(task.Deadline, FiDateFormatOptions).toUpperCase()
        );
    }
    getCommentsText(task: ITask) {
        if (!task.CommentsCount) return this.appTextService.get('common.no_comments');
        return task.CommentsCount + ' ' + this.appTextService.get('common.comments').toLowerCase();
    }
    /*getAssigneesText(task: ITask) {
        const userIds = task.AssignedUserIds;
        if (userIds.length === 1 && userIds[0] === this.userDataService.getUserId()) {
            return this.appTextService.get('task.just_you');
        }

        return userIds.length + ' ' + this.appTextService.get('task.assignees');
    }*/

    getTimeInDays(milliseconds: number) {
        if (isNullOrUndefined(milliseconds)) return;
        const daysCount = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
        return daysCount + ' ' + this.appTextService.get(daysCount === 1 ? 'common.day' : 'common.days').toLowerCase();
    }

    hasEditAccess(task: ITask) {
        return this.userDataService.getUserId() === task.CreatedByUserId;
    }

    getIsCompletedFilterByViewMode(tasksViewMode: FiTasksViewMode): boolean {
        // method for backend, should return
        // true if tasksViewMode === FiTasksViewMode.completed
        // false if tasksViewMode === FiTasksViewMode.notCompleted
        // null/undefined for other values

        if (tasksViewMode === FiTasksViewMode.notCompleted) {
            return false;
        } else if (tasksViewMode === FiTasksViewMode.completed) {
            return true;
        }

        return undefined;
    }

    //# region task parameter

    updateTaskParameter(
        parameter: ITaskParameter,
        args: {
            filterType: SideListOptionType;
            isCompleted?: boolean;
            isArchived?: boolean;
            ids?: number[];
        },
    ) {
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: false,
            FetchLightWeightObjects: false,
        };
        parameter.FilterType = args.filterType;
        parameter.IsCompleted = args.isCompleted;
        parameter.IsArchived = args.isArchived;
        parameter.Ids = args.ids ?? [];
    }

    //# endregion

    getTasksCompletedSnackbarMessage(tasksNames: string[]): string {
        if (tasksNames.length > 1) {
            return this.appTextService.get('tasks.moved_to_completed_multiple', { '{0}': tasksNames.length });
        }
        return this.appTextService.get('taskv2.moved_to_completed', { '{0}': tasksNames[0] });
    }

    getTasksCompletedUndoSnackbarMessage(tasksNames: string[]): string {
        if (tasksNames.length > 1) {
            return this.appTextService.get('taskv2.moved_to_current_multiple_undo', { '{0}': tasksNames.length });
        }
        return this.appTextService.get('taskv2.moved_to_not_started_undo', { '{0}': tasksNames[0] });
    }

    getStatusChangedSnackbar(snackbarMessage: string, buttonText: string): MatSnackBarRef<FiSnackbarComponent> {
        return this.fiSnackBarService.showSnackbar(
            {
                textMessage: snackbarMessage,
                buttonText: buttonText,
                isSnackbarBorderShown: true,
                preventButtonUppercase: true,
                buttonClass: 'BrandedColor',
            },
            false,
            'fi-snackbar-standard',
        );
    }

    isAutomatedTask(taskType: FiTaskType): boolean {
        return taskType === FiTaskType.ChecklistAutomated;
    }
}
